import styled, { css } from 'styled-components';

type styledProps = {
    size: 'xs' | 'sm' | 'lg';
    $icon?: boolean;
    $highlighted?: boolean;
    $customColor?: 'green' | 'blue';
};

const buttonCommonStyles = css<styledProps>`
    display: inline-flex;
    gap: 0 6px;
    border: none;
    align-items: center;
    cursor: pointer;
    justify-content: ${({ $icon }) => $icon ? 'center' : 'space-between'};
    transition: color 0.2s ease-in-out, background-color 0.2s ease-in-out, box-shadow 0.2s ease-in-out;
    user-select: none;
    padding: ${({
        theme,
        size,
        $icon
    }) => [size === 'lg' ? '12px' : 0, $icon ? 0 : size === 'lg' ? '60px' : theme.spacing[size === 'xs' ? '3xs' : '2xs']].join(' ')};
    width: ${({
        size,
        $icon
    }) => $icon ? (size === 'xs' ? '24px' : '36px') : 'auto'};

    font-weight: ${({ theme }) => theme.typography.weight.semiBold};
    font-family: inherit;

    text-decoration: none;

    &:focus {
        outline: none;
    }

    &:disabled {
        cursor: default;

        > * {
            cursor: default;
        }
    }

    > * {
        cursor: pointer;
    }
`;

export const buttonVariants = {
    filled: styled.button<styledProps>`
        ${buttonCommonStyles};
        border-radius: ${({ theme }) => theme.extras.radius.sm};
        height: ${({ size }) => size === 'xs' ? '24px' : (size === 'lg' ? '48px' : '36px')};

        &:disabled {
            opacity: ${({ theme }) => theme.extras.opacity.disabled};
        }

        &[data-error] {
            background-color: ${({ theme }) => theme.colors.palette.red_400};
            color: ${({ theme }) => theme.colors.palette.white};
        }

        &:not([data-error]) {
            background-color: ${({ theme }) => theme.colors.palette.blue_500};
            color: ${({ theme }) => theme.colors.palette.white};

            &:not(:disabled) {
                &:hover {
                    background-color: ${({ theme }) => theme.colors.palette.blue_600};
                    color: ${({ theme }) => theme.colors.palette.white};
                }

                &:active {
                    background-color: ${({ theme }) => theme.colors.palette.blue_700};
                    color: ${({ theme }) => theme.colors.palette.white};
                }
            }
        }
    `,
    stroke: styled.button<styledProps>`
        ${buttonCommonStyles};
        border-radius: ${({ theme }) => theme.extras.radius.sm};
        box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.palette.blue_500};
        background-color: ${({ theme }) => theme.colors.palette.white};
        height: ${({ size }) => size === 'xs' ? '24px' : '36px'};
        border: none;
        color: ${({ theme }) => theme.colors.palette.blue_500};

        &:disabled {
            opacity: ${({ theme }) => theme.extras.opacity.disabled};
        }

        &[data-error] {
            color: ${({ theme }) => theme.colors.palette.red_400};
            box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.palette.white};
        }

        &:not([data-error]) {
            &:not(:disabled) {
                &:hover {
                    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.palette.blue_600};
                    color: ${({ theme }) => theme.colors.palette.blue_600};
                    background-color: ${({ theme }) => theme.colors.palette.blue_100};
                }

                &:active {
                    box-shadow: inset 0 0 0 1px ${({ theme }) => theme.colors.palette.blue_700};
                    color: ${({ theme }) => theme.colors.palette.blue_700};
                    background-color: ${({ theme }) => theme.colors.palette.blue_200};
                }
            }
        }
    `,
    ghost: styled.button<styledProps>`
        ${buttonCommonStyles};
        border-radius: ${({ theme }) => theme.extras.radius.sm};
        background-color: transparent;
        height: ${({ size }) => size === 'xs' ? '24px' : '36px'};
        color: ${({ theme, $customColor }) => {
        if ($customColor === 'green') {
            return theme.colors.palette.green_300;
        }
        if ($customColor === 'blue') {
            return theme.colors.palette.blue_500;
        }
        return theme.colors.palette.blue_500;
    }};

        &:disabled {
            opacity: ${({ theme }) => theme.extras.opacity.disabled};
        }

        &[data-error] {
            color: ${({ theme }) => theme.colors.palette.red_400};
        }

        &:not([data-error]) {
            &:not(:disabled) {
                &:hover {
                    color: ${({ theme, $customColor }) => {
        if ($customColor === 'green') {
            return theme.colors.palette.green_300;
        }
        if ($customColor === 'blue') {
            return theme.colors.palette.blue_500;
        }
        return theme.colors.palette.blue_600;
    }};
                    background-color: ${({ theme, $customColor }) => {
        if ($customColor === 'green') {
            return theme.colors.palette.green_100;
        }
        if ($customColor === 'blue') {
            return theme.colors.palette.blue_100;
        }
        return theme.colors.palette.blue_100;
    }};
                }

                &:active {
                    color: ${({ theme, $customColor }) => {
        if ($customColor === 'green') {
            return theme.colors.palette.green_300;
        }
        if ($customColor === 'blue') {
            return theme.colors.palette.blue_500;
        }
        return theme.colors.palette.blue_700;
    }};
                    background-color: ${({ theme, $customColor }) => {
        if ($customColor === 'green') {
            return theme.colors.palette.green_200;
        }
        if ($customColor === 'blue') {
            return theme.colors.palette.blue_200;
        }
        return theme.colors.palette.blue_200;
    }};
                }
            }
        }

        ${({ $highlighted }) => $highlighted && css`
            color: ${({ theme }) => theme.colors.palette.blue_600};
            background-color: ${({ theme }) => theme.colors.palette.blue_100};
        `}
    `
};
